import React, { useState } from 'react';

import {
  ChevronRight,
  LogOut,
  Moon,
  Pencil,
  Undo,
  type LucideIcon,
} from 'lucide-react';

import Card from '../../components/common/Card';
import AdminLayout from '../../components/layout/AdminLayout';
import MobilePageBar from '../../components/layout/MobilePageBar';
import AccountSettings from '../../components/member/AccountSettings';
import { PersonalInfoCard } from '../../components/member/ProfileDetails';
import ProfileEditDrawer from '../../components/member/ProfileEditDrawer';
import { PROFILE_ROUTE } from '../../constants/GolferProfileConstants';
import { useDarkMode } from '../../hooks/useDarkMode';
import { useMyProfile } from '../../hooks/useMemberProfileApi';
import { useSessionActions } from '../../hooks/useSessionActions';

// One tappable row in a settings list: icon, label, and a chevron or a control on the right.
const SettingsRow: React.FC<{
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  trailing?: React.ReactNode;
  role?: string;
  ariaChecked?: boolean;
}> = ({ icon: Icon, label, onClick, trailing, role, ariaChecked }) => (
  <button
    type="button"
    role={role}
    aria-checked={ariaChecked}
    onClick={onClick}
    className="flex w-full items-center gap-3 border-t border-gray-100 py-3 text-left first:border-t-0 first:pt-0 last:pb-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-white/10"
  >
    <Icon className="h-5 w-5 shrink-0 text-ink-muted dark:text-white/60" />
    <span className="flex-1 text-sm font-medium text-ink dark:text-white">
      {label}
    </span>
    {trailing ?? (
      <ChevronRight className="h-5 w-5 text-ink-muted dark:text-white/40" />
    )}
  </button>
);

// Looks like the Email switch in AccountSettings. The row around it is the button, so this is
// only the picture of the switch.
const SwitchLook: React.FC<{ on: boolean }> = ({ on }) => (
  <span
    aria-hidden="true"
    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
      on ? 'bg-primary' : 'bg-gray-300 dark:bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        on ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </span>
);

// The profile's Settings page, behind the gear in the phone top bar. On a phone it is the only way
// to edit the profile, and it takes over what the desktop top bar and account menu do (dark mode,
// return to admin, log out), since the phone profile has neither. It works on a desktop too, at
// the same address, inside the normal layout.
const ProfileSettings: React.FC = () => {
  const { data: profile, isLoading, isError } = useMyProfile();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { adminId, logOut, returnToAdmin } = useSessionActions();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const breadcrumbs = [
    { label: 'My Profile', href: PROFILE_ROUTE },
    { label: 'Settings', href: '#' },
  ];

  return (
    <AdminLayout
      activeMenuItem={PROFILE_ROUTE}
      breadcrumbs={breadcrumbs}
      allowedRoles={['User']}
      mobileBar={
        <MobilePageBar
          title="Settings"
          backTo={PROFILE_ROUTE}
          backLabel="Back to my profile"
        />
      }
    >
      {isLoading ? (
        <p className="m-4 text-ink-muted">Loading your settings...</p>
      ) : isError || profile == null ? (
        <p role="alert" className="m-4 text-ink-muted">
          Your settings could not be loaded just now.
        </p>
      ) : (
        <div className="lg:max-w-2xl">
          <Card title="Profile">
            <SettingsRow
              icon={Pencil}
              label="Edit my profile"
              onClick={() => setIsEditOpen(true)}
            />
          </Card>

          <PersonalInfoCard
            profile={profile}
            onEdit={() => setIsEditOpen(true)}
          />

          <AccountSettings profile={profile} />

          <Card title="App">
            <SettingsRow
              icon={Moon}
              label="Dark mode"
              role="switch"
              ariaChecked={isDarkMode}
              onClick={toggleDarkMode}
              trailing={<SwitchLook on={isDarkMode} />}
            />
            {adminId && (
              <SettingsRow
                icon={Undo}
                label="Return to Admin"
                onClick={() => void returnToAdmin()}
              />
            )}
            <SettingsRow icon={LogOut} label="Log out" onClick={logOut} />
          </Card>

          <ProfileEditDrawer
            isOpen={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            profile={profile}
          />
        </div>
      )}
    </AdminLayout>
  );
};

export default ProfileSettings;
